import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../ui/form';
import { useCreateComment, useUpdateComment } from '../../hooks/useComments';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

// Type definition
type CommentFormData = {
  body: string;
};

interface CommentFormProps {
  productId: number;
  parentId?: number;
  commentId?: number;
  initialValue?: string;
  placeholder?: string;
  mode?: 'create' | 'edit';
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * CommentForm Component
 *
 * Handles both creating new comments and editing existing ones with:
 * - Form validation using Zod and React Hook Form
 * - Character count display
 * - Loading states and error handling
 * - Support for replies (parentId)
 * - Responsive design
 */
export function CommentForm({
  productId,
  parentId,
  commentId,
  initialValue = '',
  placeholder = 'Write your comment...',
  mode = 'create',
  onSuccess,
  onCancel,
}: CommentFormProps) {
  // Get translations
  const { getCommentsText } = useProductDetailPageTranslations();

  // Validation schema with translated messages
  const commentSchema = z.object({
    body: z
      .string()
      .min(1, getCommentsText('form.validation.commentEmpty'))
      .max(5000, getCommentsText('form.validation.commentTooLong'))
      .trim(),
  });

  const form = useForm<z.infer<typeof commentSchema>>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      body: initialValue,
    },
  });

  const createCommentMutation = useCreateComment(productId);
  const updateCommentMutation = useUpdateComment(productId, commentId || 0);

  const isEditing = mode === 'edit';
  const mutation = isEditing ? updateCommentMutation : createCommentMutation;

  // Use translated placeholder
  const translatedPlaceholder =
    placeholder === 'Write your comment...'
      ? getCommentsText('form.placeholders.writeComment')
      : placeholder;

  const watchedBody = form.watch('body');
  const characterCount = watchedBody?.length || 0;
  const maxCharacters = 5000;
  const isNearLimit = characterCount > maxCharacters * 0.8;

  const onSubmit = async (data: CommentFormData) => {
    try {
      if (isEditing && commentId) {
        await updateCommentMutation.mutateAsync({
          body: data.body,
        });
      } else {
        await createCommentMutation.mutateAsync({
          body: data.body,
          parent: parentId || null,
        });
      }

      form.reset();
      onSuccess?.();
    } catch (error) {
      // Error handling is done in the mutation hooks
      console.error('Comment submission error:', error);
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      form.handleSubmit(onSubmit)();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={translatedPlaceholder}
                  className="min-h-[100px] resize-none"
                  onKeyDown={handleKeyDown}
                  disabled={mutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Character Count */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-2">
            <span>{getCommentsText('form.hints.submitShortcut')}</span>
          </div>
          <span className={isNearLimit ? 'text-amber-600 dark:text-amber-400' : ''}>
            {getCommentsText('form.hints.characterCount', {
              current: characterCount,
              max: maxCharacters,
            })}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={mutation.isPending}
            >
              <X className="mr-1 size-4" />
              {getCommentsText('form.actions.cancel')}
            </Button>
          )}

          <Button
            type="submit"
            size="sm"
            disabled={mutation.isPending || !form.formState.isValid || characterCount === 0}
          >
            {mutation.isPending ? (
              <>
                <div className="mr-1 size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {isEditing
                  ? getCommentsText('form.actions.updating')
                  : getCommentsText('form.actions.posting')}
              </>
            ) : (
              <>
                <Send className="mr-1 size-4" />
                {isEditing
                  ? getCommentsText('form.actions.update')
                  : getCommentsText('form.actions.post')}
              </>
            )}
          </Button>
        </div>

        {/* Error Display */}
        {mutation.error && (
          <div className="text-sm text-destructive">
            {mutation.error instanceof Error
              ? mutation.error.message
              : getCommentsText('form.errors.submitFailed')}
          </div>
        )}
      </form>
    </Form>
  );
}
